import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import { useSelector } from 'react-redux'

import {
  FiArrowRight,
  FiCalendar,
  FiCheckCircle,
  FiCreditCard,
  FiHome,
  FiClock
} from 'react-icons/fi'


export default function Activity() {

  const {
    url,
    currentUser,
    transactions
  } = useSelector(state => state)

  const [application, setApplication] = useState(null)
  const [loading, setLoading] = useState(true)
  // const [error, setError] = useState('')


  useEffect(() => {
    const getLoanApplication = async () => {
      try {
        const response = await axios.get(
          `${url}/auth/my-loan-application`,
          {
            headers: {
              Authorization:
                `Bearer ${localStorage.getItem('access_token')}`
            }
          }
        )
        setApplication(response.data)
      } catch (err) {
        console.error(
          'Dashboard loan error:',
          err
        )
        // setError(
        //   err.response?.data?.detail ||
        //   'Unable to load your loan information.'
        // )
      } finally {
        setLoading(false)
      }
    }


    getLoanApplication()

  }, [url])


  const recentTransactions = useMemo(() => {

    return [...(transactions || [])]
      .sort(
        (a, b) =>
          new Date(b.paid_date || b.date) -
          new Date(a.paid_date || a.date)
      )
      .slice(0, 4)

  }, [transactions])


  const firstName =
    currentUser?.fullname
      ?.split(' ')[0] || 'there'


  if (loading) {

    return (

<main className="
  lg:ml-[15em]
  min-w-0
  min-h-screen
  flex-1
  bg-[#F7F9FC]
  px-5
  pb-10
  pt-24
  sm:px-6
  lg:px-8
  lg:pt-8
">

  <div className="
    mx-auto
    max-w-[1500px]
  ">

          <div className="
            animate-pulse
            space-y-6
          ">

            <div className="
              h-8
              bg-gray-200
              rounded-lg
              w-52
            " />

            <div className="
              h-4
              bg-gray-100
              rounded
              w-80
            " />

            <div className="
              h-52
              bg-gray-200
              rounded-3xl
            " />

          </div>

        </div>

      </main>
    )
  }


  return (

<main className="
  lg:ml-[15em]
  min-w-0
  min-h-screen
  flex-1
  bg-[#F7F9FC]
  font-[Satoshi]
  text-gray-900
  px-5
  pb-10
  pt-24
  sm:px-6
  lg:px-8
  lg:pt-8
">

  <div className="
    mx-auto
    max-w-[1500px]
  ">


        {/* HEADER */}

<div className="
  mb-6
sm:mb-8
  flex
  flex-col
  gap-4
  sm:flex-row
  sm:items-end
  sm:justify-between
">

  <div>

    <p className="
      text-sm
      font-semibold
      text-green-600
    ">
      ACCOUNT OVERVIEW
    </p>

    <h1 className="
      mt-1
      text-2xl
      sm:text-3xl
      font-bold
      tracking-tight
      text-gray-900
    ">
      Welcome back, {firstName}
    </h1>

    <p className="
      mt-2
      text-sm
      leading-6
      text-gray-500
    ">
      Here's what's happening with your RentHub account.
    </p>

  </div>


  {application?.status === 'active' ? (

    <Link
      to="/dashboard/pay"
      className="
        inline-flex
        w-fit
        items-center
        justify-center
        gap-2
        rounded-xl
        bg-green-600
        px-5
        py-3
        text-sm
        font-semibold
        text-white
        transition
        hover:bg-green-700
      "
    >
      Make a payment

      <FiArrowRight />
    </Link>

  ) : (

    <div className="
      w-fit
      rounded-full
      border
      border-green-100
      bg-green-50
      px-3.5
      py-2
      text-xs
      font-semibold
      text-green-700
    ">
      Rent financing
    </div>

  )}

</div>


        {/* NO APPLICATION */}

        {!application?.has_application && (

          <NoApplication />
        )}


        {/* APPLICATION STATES */}

        {application?.has_application &&
          application.status !== 'active' && (

          <ApplicationStatus
            application={application}
          />

        )}


        {/* ACTIVE LOAN */}

        {application?.status === 'active' &&
          application?.loan && (

          <ActiveDashboard
            application={application}
            transactions={recentTransactions}
          />

        )}

      </div>

    </main>
  )
}



function ActiveDashboard({
  application,
  transactions
}) {

  const loan = application.loan

  const totalRepayment =
    Number(loan.total_repayment || 0)

  const outstanding =
    Number(loan.outstanding_balance || 0)

  const monthlyPayment =
    Number(loan.monthly_repayment || 0)

  const installmentsLeft =
    Number(loan.installments_left || 0)

  const amountPaid =
    Math.max(
      totalRepayment - outstanding,
      0
    )

  const progress =
    totalRepayment > 0
      ? Math.min(
          (amountPaid / totalRepayment) * 100,
          100
        )
      : 0


  return (

    <div className="space-y-7">


      {/* HERO */}

      <section className="
        relative
        overflow-hidden
        bg-[#101828]
        text-white
        rounded-3xl
        px-6
        py-7
        lg:px-8
        lg:py-8
        shadow-sm
      ">

        <div className="
          absolute
          w-80
          h-80
          rounded-full
          bg-green-500/10
          -right-24
          -top-40
        " />


        <div className="
          relative
          grid
          grid-cols-1
          lg:grid-cols-[1.4fr_1fr]
          gap-8
        ">

          <div>

            <div className="
              inline-flex
              items-center
              gap-2
              bg-green-500/10
              text-green-300
              text-xs
              font-bold
              px-3
              py-1.5
              rounded-full
              mb-5
            ">

              <span className="
                w-2
                h-2
                rounded-full
                bg-green-400
              " />

              ACTIVE LOAN

            </div>


            <p className="
              text-sm
              text-gray-400
            ">
              Outstanding balance
            </p>


            <h2 className="
              text-3xl
              lg:text-4xl
              font-bold
              tracking-tight
              mt-2
            ">
              {formatCurrency(outstanding)}
            </h2>


            <div className="
              mt-7
              max-w-xl
            ">

              <div className="
                flex
                justify-between
                text-xs
                text-gray-400
                mb-2
              ">

                <span>
                  Loan repayment progress
                </span>

                <span>
                  {progress.toFixed(0)}%
                </span>

              </div>


              <div className="
                h-2
                bg-white/10
                rounded-full
                overflow-hidden
              ">

                <div
                  className="
                    h-full
                    bg-green-500
                    rounded-full
                  "
                  style={{
                    width: `${progress}%`
                  }}
                />

              </div>


              <p className="
                text-xs
                text-gray-400
                mt-3
              ">
                {formatCurrency(amountPaid)} repaid of{' '}
                {formatCurrency(totalRepayment)}
              </p>

            </div>

          </div>


          <div className="
            lg:border-l
            lg:border-white/10
            lg:pl-8
            flex
            flex-col
            justify-center
          ">

            <p className="
              text-sm
              text-gray-400
            ">
              Next repayment
            </p>

            <p className="
              text-2xl
              font-bold
              mt-2
            ">
              {formatCurrency(monthlyPayment)}
            </p>

            <p className="
              text-sm
              text-gray-400
              mt-2
            ">
              Due {formatDate(
                loan.next_payment_date
              )}
            </p>


            <Link
              to="/dashboard/pay"
              className="
                mt-6
                inline-flex
                items-center
                justify-center
                gap-2
                bg-white
                text-gray-900
                hover:bg-gray-100
                px-5
                py-3
                rounded-xl
                text-sm
                font-bold
                transition
                w-full
              "
            >
              Pay now

              <FiArrowRight />
            </Link>

          </div>

        </div>

      </section>


      {/* METRICS */}

      <div className="
        grid
        grid-cols-1
        sm:grid-cols-2
        xl:grid-cols-4
        gap-5
      ">

        <MetricCard
          icon={<FiCreditCard />}
          title="Total repayment"
          value={formatCurrency(
            totalRepayment
          )}
        />

        <MetricCard
          icon={<FiCheckCircle />}
          title="Amount repaid"
          value={formatCurrency(
            amountPaid
          )}
        />

        <MetricCard
          icon={<FiClock />}
          title="Installments left"
          value={`${installmentsLeft} of 12`}
        />

        <MetricCard
          icon={<FiCalendar />}
          title="Next payment"
          value={formatDate(
            loan.next_payment_date
          )}
          small
        />

      </div>


      {/* LOWER SECTION */}

      <div className="
        grid
        grid-cols-1
        xl:grid-cols-[0.9fr_1.4fr]
        gap-6
        items-start
      ">


        {/* LOAN DETAILS */}

        <section className="
          bg-white
          border
          border-gray-200
          rounded-2xl
          shadow-sm
          p-6
        ">

          <div className="
            flex
            items-center
            gap-3
            mb-6
          ">

            <div className="
              w-10
              h-10
              bg-green-50
              text-green-600
              rounded-xl
              flex
              items-center
              justify-center
            ">
              <FiHome />
            </div>

            <div>

              <p className="
                font-bold
                text-gray-900
              ">
                Loan details
              </p>

              <p className="
                text-xs
                text-gray-400
                mt-0.5
              ">
                Application #{application.loan_application_id}
              </p>

            </div>

          </div>


          <div className="
            bg-gray-50
            rounded-xl
            p-4
            mb-5
          ">

            <p className="
              text-xs
              uppercase
              tracking-wide
              font-semibold
              text-gray-400
            ">
              Property address
            </p>

            <p className="
              text-sm
              font-semibold
              text-gray-800
              mt-2
              leading-5
            ">
              {application.property_address}
            </p>

          </div>


          <div className="
            divide-y
            divide-gray-100
          ">

            <InfoRow
              label="Loan started"
              value={formatDate(
                loan.loan_start_date
              )}
            />

            <InfoRow
              label="Next payment"
              value={formatDate(
                loan.next_payment_date
              )}
            />

            <InfoRow
              label="Loan ends"
              value={formatDate(
                loan.loan_end_date
              )}
            />

          </div>

        </section>


        {/* RECENT TRANSACTIONS */}

        <section className="
          bg-white
          border
          border-gray-200
          rounded-2xl
          shadow-sm
          overflow-hidden
        ">

          <div className="
            flex
            justify-between
            items-center
            gap-5
            px-6
            py-5
            border-b
            border-gray-100
          ">

            <div>

              <h2 className="
                font-bold
                text-lg
              ">
                Recent repayments
              </h2>

              <p className="
                text-sm
                text-gray-500
                mt-1
              ">
                Your latest RentHub payment activity.
              </p>

            </div>


            {transactions.length > 0 && (

              <Link
                to="/dashboard/payments"
                className="
                  text-sm
                  font-semibold
                  text-green-600
                  hover:text-green-700
                  shrink-0
                "
              >
                View all
              </Link>

            )}

          </div>


          {transactions.length > 0 ? (

            <div>

              {transactions.map(
                (transaction, index) => (

                  <RecentTransaction
                    key={
                      transaction.reference ||
                      index
                    }
                    transaction={transaction}
                  />

                )
              )}

            </div>

          ) : (

            <div className="
              px-6
              py-12
              text-center
            ">

              <div className="
                w-11
                h-11
                rounded-xl
                bg-green-50
                text-green-600
                flex
                items-center
                justify-center
                mx-auto
                mb-4
              ">
                <FiCreditCard />
              </div>

              <p className="
                font-semibold
                text-gray-800
              ">
                No repayments yet
              </p>

              <p className="
                text-sm
                text-gray-500
                mt-1
              ">
                Your completed payments will appear here.
              </p>

            </div>

          )}

        </section>

      </div>

    </div>
  )
}



function NoApplication() {

  return (

    <section className="
      bg-white
      border
      border-gray-200
      rounded-3xl
      shadow-sm
      overflow-hidden
    ">

      <div className="
        grid
        grid-cols-1
        lg:grid-cols-[1.2fr_0.8fr]
      ">

        <div className="
          p-7
          lg:p-10
        ">

          <div className="
            w-12
            h-12
            rounded-xl
            bg-green-50
            text-green-600
            flex
            items-center
            justify-center
            text-xl
            mb-6
          ">
            <FiHome />
          </div>


          <p className="
            text-sm
            text-green-600
            font-semibold
          ">
            Rent financing
          </p>

          <h2 className="
            text-2xl
            lg:text-3xl
            font-bold
            mt-2
          ">
            Pay annual rent.
            <br />
            Repay monthly.
          </h2>

          <p className="
            text-gray-500
            mt-4
            leading-7
            max-w-xl
          ">
            Apply for RentHub financing and spread your annual rent repayment across twelve months.
          </p>


          <Link
            to="/apply-for-loan"
            className="
              inline-flex
              items-center
              gap-2
              bg-green-600
              hover:bg-green-700
              text-white
              font-semibold
              px-5
              py-3
              rounded-xl
              mt-7
              transition
            "
          >
            Apply for rent financing

            <FiArrowRight />
          </Link>

        </div>


        <div className="
          bg-[#101828]
          text-white
          p-7
          lg:p-10
          flex
          flex-col
          justify-center
        ">

          <p className="
            text-sm
            text-green-400
            font-semibold
          ">
            How it works
          </p>

          <div className="
            space-y-5
            mt-6
          ">

            <Step
              number="01"
              text="Submit your rent and landlord details"
            />

            <Step
              number="02"
              text="RentHub assesses your application"
            />

            <Step
              number="03"
              text="Approved rent is paid to your landlord"
            />

            <Step
              number="04"
              text="Repay RentHub monthly"
            />

          </div>

        </div>

      </div>

    </section>
  )
}



function ApplicationStatus({
  application
}) {

  const configurations = {

    under_review: {
      badge: 'Under review',
      heading: 'Your application is being reviewed',
      description:
        "Your application requires additional assessment. We'll update you when a decision has been made.",
      badgeStyle:
        'bg-amber-100 text-amber-700',
      iconStyle:
        'bg-amber-50 text-amber-600'
    },

    approved: {
      badge: 'Approved',
      heading: 'Your rent financing has been approved',
      description:
        "Your application is approved and is now waiting for landlord disbursement.",
      badgeStyle:
        'bg-green-100 text-green-700',
      iconStyle:
        'bg-green-50 text-green-600'
    },

    awaiting_disbursement: {
      badge: 'Awaiting payment',

      heading:
        'Your verification is complete',

      description:
        'Your application has passed physical verification. RentHub is preparing the approved rent payment to your landlord.',

      badgeStyle:
        'bg-blue-100 text-blue-700',

      iconStyle:
        'bg-blue-50 text-blue-600'
    },

    rejected: {
      badge: 'Declined',
      heading: 'We could not approve this application',
      description:
        "Your current rent application was not approved. You can submit a new application when you're ready.",
      badgeStyle:
        'bg-red-100 text-red-700',
      iconStyle:
        'bg-red-50 text-red-600'
    },

    physical_verification: {
      badge: 'Verification in progress',

      heading:
        'Your application has been approved',

      description:
        'RentHub is completing the required physical verification before your rent is paid to your landlord.',

      badgeStyle:
        'bg-purple-100 text-purple-700',

      iconStyle:
        'bg-purple-50 text-purple-600'
    },

  }


  const config =
    configurations[application.status]


  if (!config) {
    return null
  }


  return (

    <section className="
  bg-white
  border
  border-gray-200
  rounded-2xl
  sm:rounded-3xl
  shadow-sm
  p-5
  sm:p-7
  lg:p-10
">

      <div className="
        max-w-2xl
      ">

        <div className="
          flex
          items-center
          gap-3
          mb-5
          sm:mb-6
        ">

          <div className={`
            w-12
            h-12
            rounded-xl
            flex
            items-center
            justify-center
            text-xl
            ${config.iconStyle}
          `}>
            <FiClock />
          </div>


          <span className={`
            text-xs
            font-bold
            uppercase
            tracking-wide
            px-3
            py-1.5
            rounded-full
            ${config.badgeStyle}
          `}>
            {config.badge}
          </span>

        </div>


        <h2 className="
          text-xl
          sm:text-2xl
          font-bold
          text-gray-900
        ">
          {config.heading}
        </h2>


        <p className="
          text-sm
          sm:text-base
          text-gray-500
          leading-6
          sm:leading-7
          mt-3
        ">
          {config.description}
        </p>


        <div className="
          mt-7
          grid
          grid-cols-1
          sm:grid-cols-2
          gap-4
        ">

          {application.status ===
            'physical_verification' &&
            application.verification && (

              <VerificationProgress
                verification={
                  application.verification
                }
              />

          )}

          <MiniDetail
            label="Requested rent"
            value={formatCurrency(
              application.rent_amount
            )}
          />

          <MiniDetail
            label="Property"
            value={
              application.property_address
            }
          />

        </div>


        {application.status === 'rejected' && (

          <Link
            to="/apply-for-loan"
            className="
              inline-flex
              items-center
              gap-2
              bg-green-600
              hover:bg-green-700
              text-white
              px-5
              py-3
              rounded-xl
              font-semibold
              mt-7
              transition
            "
          >
            Apply again

            <FiArrowRight />
          </Link>

        )}

      </div>

    </section>
  )
}



function MetricCard({
  icon,
  title,
  value,
  small = false
}) {

  return (

    <div className="
      bg-white
      border
      border-gray-200
      rounded-2xl
      shadow-sm
      p-5
    ">

      <div className="
        flex
        items-center
        gap-4
      ">

        <div className="
          w-11
          h-11
          shrink-0
          rounded-xl
          bg-green-50
          text-green-600
          flex
          items-center
          justify-center
          text-xl
        ">
          {icon}
        </div>


        <div className="min-w-0">

          <p className="
            text-xs
            uppercase
            tracking-wide
            font-semibold
            text-gray-400
          ">
            {title}
          </p>

          <p className={`
            font-bold
            text-gray-900
            mt-1
            truncate

            ${
              small
                ? 'text-base'
                : 'text-xl'
            }
          `}>
            {value}
          </p>

        </div>

      </div>

    </div>
  )
}



function RecentTransaction({
  transaction
}) {

  return (

    <div className="
      flex
      items-center
      justify-between
      gap-5
      px-6
      py-5
      border-b
      border-gray-100
      last:border-b-0
      hover:bg-gray-50
      transition
    ">

      <div className="
        flex
        items-center
        gap-4
        min-w-0
      ">

        <div className="
          w-10
          h-10
          shrink-0
          bg-green-50
          text-green-600
          rounded-xl
          flex
          items-center
          justify-center
        ">
          <FiCheckCircle />
        </div>


        <div className="min-w-0">

          <p className="
            font-semibold
            text-gray-900
          ">
            Loan repayment
          </p>

          <p className="
            text-xs
            text-gray-400
            mt-1
            truncate
          ">
            {formatDate(
              transaction.paid_date ||
              transaction.date
            )}
          </p>

        </div>

      </div>


      <p className="
        font-bold
        text-gray-900
        shrink-0
      ">
        {formatCurrency(
          transaction.amount
        )}
      </p>

    </div>
  )
}



function InfoRow({
  label,
  value
}) {

  return (

    <div className="
      flex
      justify-between
      gap-5
      py-4
      first:pt-0
      last:pb-0
    ">

      <span className="
        text-sm
        text-gray-500
      ">
        {label}
      </span>

      <span className="
        text-sm
        font-semibold
        text-gray-900
        text-right
      ">
        {value}
      </span>

    </div>
  )
}



function MiniDetail({
  label,
  value
}) {

  return (

    <div className="
      bg-gray-50
      rounded-xl
      p-4
    ">

      <p className="
        text-xs
        uppercase
        tracking-wide
        font-semibold
        text-gray-400
      ">
        {label}
      </p>

      <p className="
        text-sm
        font-semibold
        text-gray-800
        mt-2
      ">
        {value || '—'}
      </p>

    </div>
  )
}



function Step({
  number,
  text
}) {

  return (

    <div className="
      flex
      items-start
      gap-4
    ">

      <span className="
        text-xs
        font-bold
        text-green-400
        mt-0.5
      ">
        {number}
      </span>

      <p className="
        text-sm
        text-gray-300
        leading-6
      ">
        {text}
      </p>

    </div>
  )
}



function formatCurrency(amount) {

  return new Intl.NumberFormat(
    'en-NG',
    {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }
  ).format(
    Number(amount || 0)
  )
}



function formatDate(date) {

  if (!date) {
    return '—'
  }

  return new Date(
    date
  ).toLocaleDateString(
    'en-GB',
    {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }
  )
}


function VerificationProgress({
  verification
}) {

  return (
    <div className="
      mt-7
      border
      border-gray-200
      rounded-2xl
      overflow-hidden
    ">

      <div className="
        bg-gray-50
        px-5
        py-4
        border-b
        border-gray-100
      ">
        <p className="
          font-semibold
          text-gray-900
        ">
          Verification progress
        </p>

        <p className="
          text-sm
          text-gray-500
          mt-1
        ">
          We'll update each step as
          verification is completed.
        </p>
      </div>

      <div className="
        divide-y
        divide-gray-100
      ">

        <VerificationRow
          label="Tenant verification"
          status={
            verification.tenant_status
          }
        />

        <VerificationRow
          label="Landlord verification"
          status={
            verification.landlord_status
          }
        />

        <VerificationRow
          label="Property verification"
          status={
            verification.property_status
          }
        />

      </div>

    </div>
  )
}


function VerificationRow({
  label,
  status
}) {

  const config = {

    passed: {
      label: 'Complete',
      style:
        'bg-green-100 text-green-700'
    },

    pending: {
      label: 'Pending',
      style:
        'bg-gray-100 text-gray-600'
    },

    in_progress: {
      label: 'In progress',
      style:
        'bg-blue-100 text-blue-700'
    },

    failed: {
      label: 'Needs review',
      style:
        'bg-amber-100 text-amber-700'
    }

  }

  const current =
    config[status] ||
    config.pending

  return (
    <div className="
      px-5
      py-4
      flex
      items-center
      justify-between
      gap-4
    ">

      <p className="
        text-sm
        text-gray-700
        font-medium
      ">
        {label}
      </p>

      <span className={`
        px-3
        py-1
        rounded-full
        text-xs
        font-bold
        ${current.style}
      `}>
        {current.label}
      </span>

    </div>
  )
}